import * as React from 'react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
import styled from 'styled-components';
import { di } from 'react-magnetic-di';
import { token } from '@atlaskit/tokens';
import type { FrameLoaderProps } from '@atlassian/help-center-common-component/iframe-loader';
import { IFrameLoader } from '@atlassian/help-center-common-component/iframe-loader';
import { ConfluenceEmbeddedView } from '../confluence-embedded-view';

export interface ConfluenceArticleIFrameProps {
    src: string;
    className?: string;
    onIFrameLoaded?: FrameLoaderProps['onIFrameLoaded'];
    articleId: string;
    hostname?: string;
    spaceKey: string | undefined;
    showEnhancedExperience: boolean;
    projectId: number | undefined;
    projectAri?: string;
    isAnonymousAccount: boolean;
    isEmbeddedRoute?: boolean;
    kbLinkDomainURLs: string[];
    isCrossSiteArticle?: boolean;
}

declare global {
    interface Window {
        getHostOffsetFunctionOverride?(hostWindow: Window): ReturnType<typeof calculateDepth>;
    }
}

const calculateDepth = (hostWindow: Window, offset = 0) => {
    let currentWindow = hostWindow;
    let hostFrameOffset = 0;

    while (currentWindow !== window.top) {
        hostFrameOffset += 1;
        currentWindow = currentWindow.parent;
    }

    return hostFrameOffset - offset;
};

const attachHostOffsetOverride = (window: Window, isEmbedView = false) => {
    const containerDepth = calculateDepth(window);

    if (isEmbedView) {
        if (window.self) {
            window.self.getHostOffsetFunctionOverride = (hostWindow: Window) =>
                calculateDepth(hostWindow, containerDepth + 1);
        } else {
            throw new Error("'self' is null on 'window'");
        }
    } else if (window.top) {
        window.top.getHostOffsetFunctionOverride = (hostWindow: Window) =>
            calculateDepth(hostWindow, containerDepth + 1);
    } else {
        throw new Error("'top' is null on 'window'");
    }
};

const detachHostOffsetOverride = (isEmbedView = false) => {
    if (isEmbedView) {
        if (window.self) {
            delete window.self.getHostOffsetFunctionOverride;
        }
    } else if (window.top) {
        delete window.top.getHostOffsetFunctionOverride;
    }
};

export class ConfluenceArticleIFrame extends React.Component<ConfluenceArticleIFrameProps> {
    componentDidMount() {
        if (this.props.isEmbeddedRoute) {
            attachHostOffsetOverride(window, true);
        } else {
            attachHostOffsetOverride(window);
        }
    }

    componentWillUnmount() {
        if (this.props.isEmbeddedRoute) {
            detachHostOffsetOverride(true);
        } else {
            detachHostOffsetOverride();
        }
    }

    render() {
        di(IFrameLoader, ConfluenceEmbeddedView);
        const {
            src,
            onIFrameLoaded,
            showEnhancedExperience,
            articleId,
            hostname,
            spaceKey,
            projectId,
            isAnonymousAccount,
            isEmbeddedRoute,
            kbLinkDomainURLs,
            projectAri,
            isCrossSiteArticle,
            ...props
        } = this.props;
        return showEnhancedExperience ? (
            <ConfluenceEmbeddedViewWrapper isEmbeddedRoute={isEmbeddedRoute}>
                <ConfluenceEmbeddedView
                    articleId={articleId}
                    spaceKey={spaceKey}
                    onIFrameLoaded={onIFrameLoaded}
                    projectId={projectId}
                    isAnonymousAccount={isAnonymousAccount}
                    kbLinkDomainURLs={kbLinkDomainURLs}
                    hostname={hostname}
                    projectAri={projectAri}
                    isCrossSiteArticle={isCrossSiteArticle}
                />
            </ConfluenceEmbeddedViewWrapper>
        ) : (
            <ConfluenceArticle {...props}>
                <IFrameLoader
                    title="confluence-article-iframe"
                    src={src}
                    sameOriginResize
                    resizePoll
                    onIFrameLoaded={onIFrameLoaded}
                />
            </ConfluenceArticle>
        );
    }
}

const confluenceArticleMargin = token('space.negative.250', '-20px');

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const ConfluenceArticle = styled.article`
    /* This works around the Confluence iframe having a padding of 20px. */
    margin: ${confluenceArticleMargin};
`;

interface ConfluenceEmbeddedViewWrapperProps {
    isEmbeddedRoute?: boolean;
}

// eslint-disable-next-line rulesdir/no-styled-export, @atlaskit/ui-styling-standard/no-exported-styles, @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
export const ConfluenceEmbeddedViewWrapper = styled.article<ConfluenceEmbeddedViewWrapperProps>`
    ${({ isEmbeddedRoute }) =>
        isEmbeddedRoute
            ? `
            height: 100vh;
    `
            : `   height: 100vh;
            min-height: 850px;
    `}
`;
